import { useEffect } from 'react';

import { onCLS, onFCP, onINP, onLCP, onTTFB } from 'web-vitals/attribution';

import type { MetricWithAttribution } from 'web-vitals/attribution';

import { getPerfContextProperties } from '@/hooks/usePerformanceTracking';

// Fullstory event properties only accept primitives, so only flat fields here.
const getAttribution = (metric: MetricWithAttribution) => {
  switch (metric.name) {
    case 'CLS': {
      const { largestShiftTarget, largestShiftValue, largestShiftTime } =
        metric.attribution;

      return {
        largest_shift_target: largestShiftTarget,
        largest_shift_value: largestShiftValue,
        largest_shift_time: largestShiftTime,
      };
    }
    case 'LCP': {
      const {
        target,
        timeToFirstByte,
        resourceLoadDelay,
        resourceLoadDuration,
        elementRenderDelay,
      } = metric.attribution;

      return {
        lcp_target: target,
        lcp_ttfb: timeToFirstByte,
        lcp_resource_load_delay: resourceLoadDelay,
        lcp_resource_load_duration: resourceLoadDuration,
        lcp_element_render_delay: elementRenderDelay,
      };
    }
    case 'INP':
      return { inp_target: metric.attribution.interactionTarget };
    default:
      return {};
  }
};

/**
 * Reports Web Vitals (LCP, FCP, CLS, INP, TTFB) to FullStory as perf events.
 * Call once at app root. Each metric fires exactly once per page load.
 *
 * CLS/LCP/INP carry attribution (the element responsible) so slow or shifting
 * routes can be traced to a selector. In this SPA, CLS accumulates across route
 * changes within one page load, so the event URL is where the session ended,
 * not necessarily where the shift happened — use `largest_shift_target`.
 */
export function useWebVitals() {
  useEffect(() => {
    const report = (metric: MetricWithAttribution) => {
      window.FS?.('trackEvent', {
        name: `web_vital_${metric.name.toLowerCase()}`,
        properties: {
          metric_name: metric.name,
          value: metric.value,
          rating: metric.rating,
          delta: metric.delta,
          navigation_type: metric.navigationType,
          ...getAttribution(metric),
          ...getPerfContextProperties(),
        },
      });

      if (import.meta.env.DEV) {
        console.debug('[web-vital]', metric.name, metric.value, metric.rating);
      }
    };

    onCLS(report);
    onFCP(report);
    onINP(report);
    onLCP(report);
    onTTFB(report);
  }, []);
}
